import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithReauth } from '../../../shared/api/baseQuery';

const normalizeListResponse = (response) => ({
  data: response?.results || response || [],
  total: response?.count || response?.length || 0,
});

export const aiGovernanceApi = createApi({
  reducerPath: 'aiGovernanceApi',
  baseQuery: baseQueryWithReauth,
  tagTypes: ['AISystem', 'AIGovernanceDashboard', 'AIAssessment', 'AIIncident'],
  endpoints: (builder) => ({
    getAISystems: builder.query({
      query: ({ page = 1, limit = 20, filters = {} } = {}) => {
        const params = new URLSearchParams();
        params.set('page', page.toString());
        params.set('page_size', limit.toString());

        if (filters.riskClassification && filters.riskClassification !== 'all') {
          params.set('risk_classification', filters.riskClassification);
        }
        if (filters.assessmentStatus && filters.assessmentStatus !== 'all') {
          params.set('ai_impact_assessment_status', filters.assessmentStatus);
        }
        if (filters.oversightLevel && filters.oversightLevel !== 'all') {
          params.set('human_oversight_level', filters.oversightLevel);
        }

        return {
          url: `/api/v1/ai-governance/systems/?${params.toString()}`,
        };
      },
      transformResponse: normalizeListResponse,
      providesTags: (result) =>
        result?.data
          ? [
              ...result.data.map(({ id }) => ({ type: 'AISystem', id })),
              { type: 'AISystem', id: 'LIST' },
            ]
          : [{ type: 'AISystem', id: 'LIST' }],
    }),
    getAIGovernanceDashboard: builder.query({
      query: () => ({
        url: '/api/v1/ai-governance/systems/dashboard/',
      }),
      providesTags: ['AIGovernanceDashboard'],
    }),
    getAISystemById: builder.query({
      query: (id) => `/api/v1/ai-governance/systems/${encodeURIComponent(id)}/`,
      providesTags: (result, error, id) => [{ type: 'AISystem', id }],
    }),
    createAISystem: builder.mutation({
      query: (payload) => ({
        url: '/api/v1/ai-governance/systems/',
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: [{ type: 'AISystem', id: 'LIST' }, 'AIGovernanceDashboard'],
    }),
    updateAISystem: builder.mutation({
      query: ({ id, ...payload }) => ({
        url: `/api/v1/ai-governance/systems/${id}/`,
        method: 'PATCH',
        body: payload,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'AISystem', id },
        { type: 'AISystem', id: 'LIST' },
        'AIGovernanceDashboard',
      ],
    }),
    deleteAISystem: builder.mutation({
      query: (id) => ({
        url: `/api/v1/ai-governance/systems/${id}/`,
        method: 'DELETE',
      }),
      invalidatesTags: [{ type: 'AISystem', id: 'LIST' }, 'AIGovernanceDashboard'],
    }),

    // ── AIG-03: ISO/IEC 42001 aims_status workflow ──────────────────────────
    // apps.shared.workflow.WorkflowViewMixin gives every workflow_key='ai_governance'
    // ViewSet these 3 actions server-side; nothing on the FE called them before this
    // fix (BE-NO-FE gap).
    getAISystemAvailableTransitions: builder.query({
      query: (id) => `/api/v1/ai-governance/systems/${id}/transitions/available/`,
      providesTags: (result, error, id) => [{ type: 'AISystem', id }],
    }),
    getAISystemTransitionHistory: builder.query({
      query: (id) => `/api/v1/ai-governance/systems/${id}/transitions/history/`,
      providesTags: (result, error, id) => [{ type: 'AISystem', id }],
    }),
    transitionAISystem: builder.mutation({
      // C2 (Task 11): moving to under_review opens the approval; `approvers` (picks keyed
      // by flow step order) only when a step needs a choice (400 needs_choice otherwise).
      query: ({ id, to_state, comment, evidence_ref, notification_targets, approvers }) => ({
        url: `/api/v1/ai-governance/systems/${encodeURIComponent(id)}/transition/`,
        method: 'POST',
        body: {
          to_state,
          comment,
          evidence_ref,
          notification_targets,
          ...(approvers ? { approvers } : {}),
        },
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'AISystem', id },
        { type: 'AISystem', id: 'LIST' },
        'AIGovernanceDashboard',
      ],
    }),

    // ── AI Impact Assessments ─────────────────────────────────────────────
    getAIAssessments: builder.query({
      query: ({ systemId, outcome, page = 1 } = {}) => {
        const params = new URLSearchParams({ page, page_size: '50' });
        if (systemId) params.set('ai_system', systemId);
        if (outcome) params.set('outcome', outcome);
        return { url: `/api/v1/ai-governance/assessments/?${params}` };
      },
      transformResponse: (r) => ({ data: r?.results || r || [], total: r?.count || 0 }),
      providesTags: (result) =>
        result?.data
          ? [
              ...result.data.map(({ id }) => ({ type: 'AIAssessment', id })),
              { type: 'AIAssessment', id: 'LIST' },
            ]
          : [{ type: 'AIAssessment', id: 'LIST' }],
    }),
    createAIAssessment: builder.mutation({
      query: (payload) => ({
        url: '/api/v1/ai-governance/assessments/',
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: [
        { type: 'AIAssessment', id: 'LIST' },
        { type: 'AISystem', id: 'LIST' },
        'AIGovernanceDashboard',
      ],
    }),

    // ── AI Incidents ──────────────────────────────────────────────────────
    getAIIncidents: builder.query({
      query: ({ systemId, severity, status, page = 1 } = {}) => {
        const params = new URLSearchParams({ page, page_size: '50' });
        if (systemId) params.set('ai_system', systemId);
        if (severity) params.set('severity', severity);
        if (status) params.set('status', status);
        return { url: `/api/v1/ai-governance/incidents/?${params}` };
      },
      transformResponse: (r) => ({ data: r?.results || r || [], total: r?.count || 0 }),
      providesTags: (result) =>
        result?.data
          ? [
              ...result.data.map(({ id }) => ({ type: 'AIIncident', id })),
              { type: 'AIIncident', id: 'LIST' },
            ]
          : [{ type: 'AIIncident', id: 'LIST' }],
    }),
    createAIIncident: builder.mutation({
      query: (payload) => ({
        url: '/api/v1/ai-governance/incidents/',
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: [{ type: 'AIIncident', id: 'LIST' }],
    }),
    updateAIIncident: builder.mutation({
      query: ({ id, ...payload }) => ({
        url: `/api/v1/ai-governance/incidents/${id}/`,
        method: 'PATCH',
        body: payload,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'AIIncident', id },
        { type: 'AIIncident', id: 'LIST' },
      ],
    }),
  }),
});

export const {
  useGetAISystemsQuery,
  useGetAISystemByIdQuery,
  useGetAIGovernanceDashboardQuery,
  useCreateAISystemMutation,
  useUpdateAISystemMutation,
  useDeleteAISystemMutation,
  useGetAISystemAvailableTransitionsQuery,
  useGetAISystemTransitionHistoryQuery,
  useTransitionAISystemMutation,
  useGetAIAssessmentsQuery,
  useCreateAIAssessmentMutation,
  useGetAIIncidentsQuery,
  useCreateAIIncidentMutation,
  useUpdateAIIncidentMutation,
} = aiGovernanceApi;
